import type {
  AllHTMLAttributes,
  FormEvent,
  ReactNode,
  Ref,
  SyntheticEvent,
} from 'react';

import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import { Box } from '../box/box';
import type { FieldContainerBaseProps } from '../field-container/field-container';
import { FieldContainer } from '../field-container/field-container';
import { textVariantToStyles } from '../text/text-styles';

import { getInputFieldStyles } from './get-input-field-styles';

const sizeConfig = {
  medium: { paddingY: 'baseNeg2' as const },
  large: { paddingY: 'base' as const }, // TODO: base should be basePos1, but I don't want to cause regressions on components that use this.
};

export type InputFieldSize = keyof typeof sizeConfig;

export interface InputFieldProps
  extends FieldContainerBaseProps,
    Partial<
      Pick<
        AllHTMLAttributes<HTMLInputElement>,
        'placeholder' | 'required' | 'autoComplete'
      >
    > {
  id: string;
  onFocus?: (event: SyntheticEvent) => void;
  onBlur?: (event: FormEvent<HTMLInputElement>) => void;
  onChange?: (event: FormEvent<HTMLInputElement>) => void;
  defaultValue?: string;
  className?: string;
  extraContainerClasses?: string;
  type?: 'text' | 'password' | 'tel' | 'search';
  'data-testid'?: string;
  'aria-describedby'?: string;
  children?: ReactNode;
  validating?: boolean;
  size?: InputFieldSize;
  /**
   * Override of logic to show outline error styling on input field without
   * validation messages. e.g. when using this component to compose compound
   * components with multiple inputs and shared validation.
   */
  showVisualErrorStyles?: boolean;
  ref?: Ref<HTMLInputElement>;
}

const TEST_IDS = {
  TEXT_INPUT: 'text-input',
  CONTROL_CONTAINER: 'text-input-control-container',
};

export const InputField = ({
  defaultValue,
  onFocus,
  onBlur,
  onChange,
  extraContainerClasses,
  label,
  screenReaderOnlyLabel = false,
  helpText,
  disabled = false,
  type = 'text',
  'data-testid': testId = TEST_IDS.TEXT_INPUT,
  className,
  children,
  placeholder,
  errors,
  'aria-invalid': invalid,
  'aria-describedby': errorMessageId,
  id,
  name,
  showVisualErrorStyles = false,
  required = false,
  readOnly = false,
  autoComplete,
  showField,
  size = 'large', // TODO: Should probably default to medium, but I don't want to cause regressions on components that use this.
  ref,
  ...inputProps // we want to spread in any other props that are passed in by libraries, e.g. Conform
}: InputFieldProps) => {
  const { inputResetStyles, stateStyles, boxProps } = getInputFieldStyles({
    hasAdjacentSibling: !!children,
    disabled: disabled || readOnly,
    error: invalid || showVisualErrorStyles,
  });

  return (
    <FieldContainer
      screenReaderOnlyLabel={screenReaderOnlyLabel}
      htmlFor={id}
      name={name}
      label={label}
      errors={errors}
      aria-invalid={invalid}
      errorMessageId={errorMessageId}
      disabled={disabled}
      helpText={helpText}
      showField={showField}
      readOnly={readOnly}
    >
      <Box
        stack="row"
        {...boxProps}
        className={classFilter([stateStyles, extraContainerClasses])}
        data-testid={TEST_IDS.CONTROL_CONTAINER}
      >
        <Box<HTMLInputElement>
          {...inputProps}
          id={id}
          Component="input"
          color="currentColor"
          rounded
          expand
          variant="initial"
          className={classFilter([
            inputResetStyles,
            className,
            textVariantToStyles('input'),
          ])}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          padding={{
            y: sizeConfig[size].paddingY,
            left: 'base',
            right: !children ? 'base' : 'none',
          }}
          placeholder={placeholder}
          type={type}
          defaultValue={defaultValue || ''}
          data-testid={testId}
          disabled={disabled}
          readOnly={readOnly}
          name={name}
          aria-invalid={invalid}
          aria-errormessage={errorMessageId} // https://stackoverflow.com/questions/51554395/what-is-the-difference-between-aria-errormessage-and-aria-alert
          // Note this is aria-required to indicate to screen-readers it
          // is a required field. If we used ‘required’ it would block submit
          // if the field is empty, however Blurr handles feedback here.
          aria-required={required}
          ref={ref}
          autoComplete={autoComplete}
        />
        {children}
      </Box>
    </FieldContainer>
  );
};
